import React from 'react';
import { Box, Grid2 as Grid, Typography } from '@mui/material';

import { colorMap } from '../components/icons/Occupancy';
import { delayLabel } from '../utils/liveTime';
import { useI18n } from '../i18n/I18nContext';
import type { DerivedStop } from './deriveJourney';

/**
 * A stop the train has already left.
 *
 * Compact by design: a past stop is there to answer "what did we measure", which a
 * colour chip and a passenger count convey. Full carriage graphics for all ~24 stops
 * would push the current stop off screen.
 *
 * NO PERCENTAGE. Occupancy is shown as a passenger count and a level only. A
 * load percentage invites "100% full", which is not a claim this board should make:
 * capacity here is a seated-plus-standing figure from the VU message, not a legal
 * limit, and a train at "100%" is a normal peak-hour train.
 *
 * Where a measurement exists and the one-stop-out forecast was retained beside it,
 * the difference is shown as a passenger delta — the accuracy signal the occupancy
 * document keeps `forecast_by_horizon["1"]` for.
 */

const CHIP_BG: Record<string, string> = {
  MEASURED: 'transparent',
  MISSING: '#fdecec',
  AWAITING: '#f4f4f4',
  NONE: 'transparent',
  FORECAST: '#fdf0cc',
};

const StopRowPast: React.FC<{ stop: DerivedStop }> = ({ stop }) => {
  const { t } = useI18n();
  const occ = stop.occupancy;
  const dot = occ.level ? colorMap[occ.level] : '#d0d4d9';

  const arr = stop.arrival.obsLabel ?? stop.arrival.schedLabel;
  const dep = stop.departure.obsLabel ?? stop.departure.schedLabel;
  const d = delayLabel(stop.departure.delaySec ?? stop.arrival.delaySec);

  const err = occ.state === 'MEASURED' ? occ.errorVsH1 : null;
  const errPax: number | null = err?.passengers ?? null;
  const acc = occ.accuracyPct;
  const forecastPax = occ.forecastH1?.train?.passengers ?? null;

  const chipText =
    occ.state === 'MISSING' ? t('occ.missing')
    : occ.state === 'AWAITING' ? t('occ.awaiting')
    : occ.state === 'FORECAST' ? t('occ.forecast')
    : '—';

  return (
    <Grid
      container
      spacing={0}
      sx={{
        minWidth: '622px',
        py: '5px',
        borderBottom: '1px solid #f0f2f4',
        opacity: stop.cancelled ? 0.45 : 0.78,
        textDecoration: stop.cancelled ? 'line-through' : 'none',
      }}
    >
      <Grid size={4}>
        <Typography fontSize={13.5} color="#37474f" align="left" noWrap>
          <Box
            component="span"
            sx={{
              display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%',
              backgroundColor: dot, mr: '8px', ml: '4px',
              border: occ.state === 'MEASURED' ? 'none' : '1px solid #b6bcc4',
            }}
          />
          {stop.name}
        </Typography>
      </Grid>

      <Grid size={5}>
        <Typography fontSize={13} align="left" noWrap>
          {occ.state === 'MEASURED' ? (
            <>
              <Box component="span" sx={{ fontWeight: 600, color: '#263238' }}>
                {occ.passengers ?? '—'}
              </Box>
              {errPax !== null && (
                <Box
                  component="span"
                  title={
                    `${t('occ.delta')}`
                    + (forecastPax !== null ? ` — ${t('occ.forecast')} ${forecastPax}` : '')
                    + (err?.model_version ? ` · ${err.model_version}` : '')
                  }
                  sx={{ ml: '8px', fontSize: '9.5px', color: errPax === 0 ? '#78909c' : '#5c6bc0' }}
                >
                  {t('occ.variance')} Δ{errPax > 0 ? '+' : ''}{errPax}
                  {acc !== null && (
                    <Box component="span" sx={{ ml: '3px', fontWeight: 700 }}>
                      ({acc}%)
                    </Box>
                  )}
                </Box>
              )}
            </>
          ) : (
            <Box
              component="span"
              title={occ.reason ? t(`reason.${occ.reason}`) : undefined}
              sx={{
                px: '5px', py: '1px', fontSize: '10px', fontWeight: 600,
                borderRadius: '3px', backgroundColor: CHIP_BG[occ.state] ?? 'transparent',
                color: occ.state === 'MISSING' ? '#b3261e' : '#78909c',
                border: occ.state === 'MISSING' ? '1px solid #f0b4b0' : '1px solid #e0e0e0',
              }}
            >
              {chipText}
            </Box>
          )}
        </Typography>
      </Grid>

      <Grid size={3}>
        <Typography fontSize={12.5} color="#546e7a" align="right" noWrap>
          {arr && (
            <Box component="span" sx={{ whiteSpace: 'nowrap', ml: '8px' }}>
              <Box component="span" sx={{ color: '#9e9e9e', fontSize: '11.5px' }}>{t('time.arr')} </Box>
              {arr}
            </Box>
          )}
          {dep && (
            <Box component="span" sx={{ whiteSpace: 'nowrap', ml: '8px' }}>
              <Box component="span" sx={{ color: '#9e9e9e', fontSize: '11.5px' }}>{t('time.dep')} </Box>
              {dep}
            </Box>
          )}
          {d && (
            <Box component="span" sx={{ color: '#d32f2f', fontWeight: 600, ml: '5px' }}>({d})</Box>
          )}
        </Typography>
      </Grid>
    </Grid>
  );
};

export default StopRowPast;
